import {sendAnalyticsEvent} from '#/analytics/analytics';
import {WalletContext} from '#/contexts/wallet-provider';
import {useAccountWishlist} from '#/hooks/query/use-wishlist';
import {useBlockingLoginAction} from '#/hooks/use-blocking-login-action';
import {apiWishlistRequests} from '#/repository/api-wishlist-requests';
import {removeDoubleAndStartingSpaces} from '#/utils/string-utils';
import {ArrowPathIcon, StarIcon} from '@heroicons/react/24/solid';
import {Land, LandMarketInfo} from '@shareland/shareland-common';
import {useMutation} from '@tanstack/react-query';
import {FunctionComponent, useContext} from 'react';

const styles = {
  dark: {
    iconWidth: 'w-7',
    starStrokeUnselected: 'stroke-white',
    starStrokeUnselectedHover: false,
  },
};

export const WishlistButton: FunctionComponent<{
  land: Land;
  landMarketInfo: LandMarketInfo | undefined;
  className?: string;
  style?: typeof styles.dark;
}> = ({land, landMarketInfo, className, style = styles.dark}) => {
  const {
    accountResult,
    web3AuthSignatureResult: {data: web3AuthSignatureGroup},
  } = useContext(WalletContext);
  const {address: account, isConnected} = accountResult;

  const {data: wishlist, refetch: refetchWishlist} = useAccountWishlist(account);
  const isLandInWishlist = wishlist && wishlist.findIndex(elem => elem.id === land?.id) !== -1;
  const isLoggedInAndWishlistDidntLoad = isConnected && wishlist === undefined;

  const updateWishlistMutation = useMutation({
    mutationKey: ['update-wishlist-mutation', isLandInWishlist],
    mutationFn: async () => {
      if (!web3AuthSignatureGroup) return;

      sendAnalyticsEvent.favoriteProperty({
        land,
        landMarketInfo,
        value: !isLandInWishlist,
      });
      const result = await (!isLandInWishlist
        ? apiWishlistRequests.add(
            {
              landId: land!.id,
            },
            web3AuthSignatureGroup,
          )
        : apiWishlistRequests.remove(
            {
              landId: land!.id,
            },
            web3AuthSignatureGroup,
          ));
      if (!!result) {
        await refetchWishlist();
      }
    },
  });

  const onWishlistToggle = useBlockingLoginAction(updateWishlistMutation.mutateAsync);
  const onClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.stopPropagation();
    onWishlistToggle();
  };

  const buttonClassName = removeDoubleAndStartingSpaces(`
  group/wishlistbutton
  flex-center
  p-0
  rounded-full
  hover:opacity-70
  disabled:opacity-50
  ${className ?? ''}
  `);

  const displayLoadingIcon = updateWishlistMutation.isPending;

  return (
    <button
      onClick={onClick}
      className={buttonClassName}
      disabled={updateWishlistMutation.isPending || isLoggedInAndWishlistDidntLoad}
    >
      {displayLoadingIcon ? (
        <ArrowPathIcon className='w-5 h-5 stroke-green-500 fill-green-500 animate-spin' />
      ) : (
        <StarIcon
          className={`${style.iconWidth} aspect-square ${
            isLandInWishlist
              ? 'stroke-deepblue fill-deepblue'
              : `${style.starStrokeUnselected} ${
                  style.starStrokeUnselectedHover ? 'group-hover/wishlistbutton:stroke-yellow-500' : ''
                } fill-transparent`
          }`}
        />
      )}
    </button>
  );
};
